import { authClient } from '~~/layers/base/lib/auth-client'
import { useClearClientSessionOnLogout } from '~~/layers/base/composables/useClearClientSessionOnLogout'
import { useFeatureFlagsStore } from '~~/layers/base/stores/featureFlags.store'
import { useNavigationStackStore } from '~~/layers/base/stores/navigationStack.store'

/**
 * Budget for the "Signing out..." lock. `useScreenLock` defaults to a 10s auto-unlock, which the
 * sequence below can now exceed: cache clears, a route change, `signOut`, `signIn.anonymous`, a
 * second clear, and finally the flag re-resolve (up to 5s of its own). Raised so the safety net
 * stays a backstop rather than dropping the overlay mid-logout.
 */
const LOGOUT_LOCK_TIMEOUT_MS = 30_000

/**
 * Handwriting designer routes share one personalize page with purchase; path check mirrors
 * `redirectHomeAfterLogout` on `/handwritten-note/personalize/...` (guests should not stay in-flow).
 */
function isDesignerHandwritingLogoutHomePath(path: string): boolean {
    return (
        path.startsWith('/designer/personalize/handwriting/') ||
        path.startsWith('/designer/start/handwriting/')
    )
}

/**
 * Full Better Auth logout for the Capacitor / express shell: end the credential session, then
 * **always** call `signIn.anonymous()` so the SPA returns to a guest session (the anonymous plugin
 * forbids a second anonymous sign-in while already anonymous, so this is safe after `signOut`).
 *
 * After auth swap, clears Send Handwritten Note / checkout client session (TK-12970).
 *
 * Post-logout navigation (TK-13098) — Capacitor / redirect-home routes, under
 * {@link useScreenLock} (`persistOnRouteChange` so the overlay survives the route change):
 * 1. Clear Home-scoped client caches **without** wiping the profile store (Account stays visually
 *    intact while we leave it).
 * 2. Navigate to `/` while still signed in.
 * 3. Sign out → anonymous → finish clearing (including profile store) on Home.
 * 4. Unlock onto a fresh guest Home.
 *
 * Web default: stay on the current route after sign-out (`ProfileLoggedOut` on `/profile`).
 */
export function useBetterAuthLogout() {
    const route = useRoute()
    const { lock, unlock } = useScreenLock()
    const datadog = useDatadog()
    const runtimeConfig = useRuntimeConfig() as { public?: { NUXT_CAPACITOR_APP?: boolean } }
    const isCapacitorApp = computed(() => Boolean(runtimeConfig.public?.NUXT_CAPACITOR_APP))
    const { pauseAutosaveBeforeAnonymousRestore, clearClientSessionOnLogout } =
        useClearClientSessionOnLogout()
    const navigationStackStore = useNavigationStackStore()
    const featureFlagsStore = useFeatureFlagsStore()

    function shouldNavigateHomeAfterLogout(): boolean {
        if (isCapacitorApp.value) {
            return true
        }
        return (
            route.meta.redirectHomeAfterLogout === true ||
            isDesignerHandwritingLogoutHomePath(route.path)
        )
    }

    async function clearSessionAfterAuthSwap(): Promise<void> {
        try {
            await clearClientSessionOnLogout()
        } catch (clearError) {
            const err = clearError instanceof Error ? clearError : new Error(String(clearError))
            datadog.logError(err, {
                feature: 'better-auth',
                action: 'clearClientSessionOnLogout',
            })
        }
    }

    async function logoutAfterBetterAuth(): Promise<void> {
        // Existing ScreenLockOverlay only — keep it across Home nav (default lock clears on route).
        await lock({
            message: 'Signing out...',
            persistOnRouteChange: true,
            timeoutMs: LOGOUT_LOCK_TIMEOUT_MS,
        })
        try {
            // Cancel before anon restore so a pending PATCH cannot write under the new guest
            // (merge-on-link would otherwise attach that work to the next credential sign-in).
            pauseAutosaveBeforeAnonymousRestore()

            const goHome = shouldNavigateHomeAfterLogout()
            if (goHome) {
                // Fresh Home under the lock; leave Account's profile UI dirty until we navigate away.
                try {
                    await clearClientSessionOnLogout({ preserveProfileStore: true })
                } catch (clearError) {
                    const err =
                        clearError instanceof Error ? clearError : new Error(String(clearError))
                    datadog.logError(err, {
                        feature: 'better-auth',
                        action: 'clearClientSessionOnLogout',
                        phase: 'pre-navigate-home',
                    })
                }
                if (isCapacitorApp.value) {
                    navigationStackStore.resetAllStacksToHome()
                }
                await navigateTo('/', { replace: true })
            }

            await authClient.signOut()
            const anonResult = await authClient.signIn.anonymous()
            if (anonResult.error) {
                datadog.logError(new Error(anonResult.error.message), {
                    feature: 'better-auth',
                    action: 'signIn.anonymous',
                    phase: 'post-logout',
                })
            }

            await clearSessionAfterAuthSwap()

            // Still under the lock: re-resolve flags for the restored guest session before the
            // overlay drops, so no flag-gated surface renders against the signed-out account's
            // per-user targeted map (TK-13203). Must follow `signIn.anonymous()` — running it
            // earlier would resolve against the old cookie.
            //
            // Capacitor only by scope. Never throws: `refresh` settles on fail-closed defaults on
            // failure or timeout, so this cannot strand the user behind the overlay.
            //
            // Safe against re-entrancy: `useApiFetch` fires `handleSessionExpired()` on any 401
            // from a relative `/api/...` URL, but `/api/feature-flags` never 401s (it fails closed
            // and returns a full map), and `handleSessionExpired` re-checks the session anyway —
            // which by now reports the anonymous user, so it returns early.
            if (isCapacitorApp.value) {
                await featureFlagsStore.refresh({ failClosed: true })
            }
        } catch (error) {
            const err = error instanceof Error ? error : new Error(String(error))
            datadog.logError(err, { feature: 'better-auth', action: 'logout' })
            throw err
        } finally {
            unlock()
        }
    }

    return { logoutAfterBetterAuth }
}
